import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import { Badge, Button, StatusDot } from '@/components/atoms';
import { articlesApi } from '@/services/articles';
import { intelligenceService, type NewsCard } from '@/services/intelligence';
import { sitesApi } from '@/services/config';
import { ChipPicker, type ChipOption } from '@/components/ChipPicker/ChipPicker';
import { MediaPicker } from '@/components/MediaPicker/MediaPicker';
import { RichTextEditor } from '@/components/RichTextEditor/RichTextEditor';
import { SeoPanel } from '@/components/SeoPanel/SeoPanel';
import {
  useArticle,
  useArticleHistory,
  useCategories,
  useTagsByIds,
  useTagsSearch,
  useUpdateArticle,
} from '@/hooks/useArticles';
import {
  useAddManifestation,
  useCreateStory,
  useStoriesByRef,
} from '@/hooks/useStories';
import type { MediaItem } from '@/services/media';

import styles from './ArticleEditor.module.css';

type FeedbackKind = 'success' | 'error' | 'info';

interface Feedback {
  kind: FeedbackKind;
  message: string;
}

function describeError(error: unknown): string {
  const raw = String(error);
  // ApiError prints like "502 Bad Gateway"; detail is in body — TanStack carries the Error
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return raw;
}

export function ArticleEditor({
  idProp,
  siteProp,
  embedded = false,
  onClose,
}: {
  idProp?: number;
  siteProp?: string;
  embedded?: boolean;
  onClose?: () => void;
} = {}) {
  const params = useParams<{ id: string }>();
  const id = idProp ?? Number(params.id);
  const [searchParams] = useSearchParams();
  const site = siteProp ?? (searchParams.get('site') || undefined);
  const navigate = useNavigate();

  // Em modal (embedded), "voltar/cancelar" fecha o modal em vez de navegar.
  const closeOrBack = () => (embedded && onClose ? onClose() : navigate('/artigos'));
  const { data: article, isLoading, error } = useArticle(id, site);
  // R6: PAA real da origem do artigo → alimenta o SeoPanel (inserir como H3).
  const { data: origemData } = useQuery({
    queryKey: ['article', 'origem', id, site ?? ''],
    queryFn: () => articlesApi.origem(id, site),
    enabled: Number.isFinite(id),
    staleTime: 60_000,
  });
  const origemQuery =
    typeof origemData?.contexto?.query === 'string' ? origemData.contexto.query : undefined;
  const { data: origemTopic } = useQuery({
    queryKey: ['intel', 'topic', origemQuery],
    queryFn: () => intelligenceService.getTopic({ query: origemQuery as string }),
    enabled: !!origemQuery,
    staleTime: 300_000,
  });
  const paaReal = origemTopic?.paa ?? [];
  const { data: history } = useArticleHistory(id);
  const { data: categories } = useCategories(site); // D-6: categorias do site do artigo
  const { data: linkedStories } = useStoriesByRef(
    'wp_post',
    Number.isFinite(id) ? String(id) : undefined,
  );
  const updateMutation = useUpdateArticle();
  const createStoryMutation = useCreateStory();
  const addManifestation = useAddManifestation();
  const { data: sites } = useQuery({
    queryKey: ['wp-sites', 'active'],
    queryFn: () => sitesApi.list(true),
    staleTime: 5 * 60_000,
  });
  const [targetSite, setTargetSite] = useState('');
  const [tweetText, setTweetText] = useState('');
  const [tweetCopied, setTweetCopied] = useState(false);

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [featuredMedia, setFeaturedMedia] = useState<number | null>(null);
  const [featuredMediaUrl, setFeaturedMediaUrl] = useState<string | null>(null);
  const [categoryIds, setCategoryIds] = useState<number[]>([]);
  const [tagQuery, setTagQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<{ id: number; name: string }[]>([]);
  const [dirty, setDirty] = useState(false);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);

  const { data: tagSearchResults, isLoading: tagSearching } = useTagsSearch(tagQuery, site);
  const { data: namedTags } = useTagsByIds(article?.tags ?? [], site);

  useEffect(() => {
    if (article) {
      setTitle(article.title);
      setContent(article.content_html);
      setExcerpt(article.excerpt ?? '');
      setFeaturedMedia(article.featured_media?.id ?? null);
      setFeaturedMediaUrl(article.featured_media?.source_url ?? null);
      setCategoryIds(article.categories);
      // Tags arrive as IDs only; useTagsByIds resolves names below.
      setSelectedTags(article.tags.map((tid) => ({ id: tid, name: `tag #${tid}` })));
      setDirty(false);
      setFeedback(null);
    }
  }, [article]);

  // Once the WP /tags?include= call resolves, swap placeholder names in.
  useEffect(() => {
    if (!namedTags || namedTags.length === 0) return;
    setSelectedTags((prev) => {
      const byId = new Map(namedTags.map((t) => [t.id, t.name]));
      let changed = false;
      const next = prev.map((t) => {
        const real = byId.get(t.id);
        if (real && real !== t.name) {
          changed = true;
          return { id: t.id, name: real };
        }
        return t;
      });
      return changed ? next : prev;
    });
  }, [namedTags]);

  const categoryOptions: ChipOption[] = useMemo(() => {
    if (!categories) return [];
    return categories
      .filter((c) => c.count > 0)
      .sort((a, b) => b.count - a.count)
      .map((c) => ({ id: c.id, label: c.name, hint: `${c.count}` }));
  }, [categories]);

  const tagOptions: ChipOption[] = useMemo(() => {
    const fromSearch = (tagSearchResults ?? []).map((t) => ({
      id: t.id,
      label: t.name,
      hint: `${t.count}`,
    }));
    // Combine with currently selected so chips render properly
    const selectedOptions = selectedTags.map((t) => ({ id: t.id, label: t.name }));
    const seen = new Set<number>();
    return [...selectedOptions, ...fromSearch].filter((o) => {
      if (seen.has(o.id)) return false;
      seen.add(o.id);
      return true;
    });
  }, [tagSearchResults, selectedTags]);

  // Mutations precisam ser chamadas SEMPRE (antes de qualquer early return) —
  // senão a contagem de hooks muda entre renders (React error #310).
  const crossPublish = useMutation({
    mutationFn: () =>
      articlesApi.publishTo({
        target_site: targetSite,
        title,
        content,
        excerpt,
        status: 'publish',
        featured_media_url: featuredMediaUrl ?? undefined,
        // D-5: manda NOMES; o destino resolve (IDs de categoria são por site).
        category_names: (categories ?? [])
          .filter((c) => categoryIds.includes(c.id))
          .map((c) => c.name),
      }),
    onSuccess: (r) =>
      setFeedback({
        kind: r.image_warning ? 'info' : 'success',
        message: `Publicado em ${r.target_site} (#${r.wp_id})${
          r.image_warning ? ' — atenção: ' + r.image_warning : ' (com imagem)'
        }.`,
      }),
    onError: (e) => setFeedback({ kind: 'error', message: describeError(e) }),
  });
  const genTweet = useMutation({
    mutationFn: () =>
      articlesApi.generateTweet({
        title,
        excerpt: excerpt || undefined,
        content,
        link: article?.link,
      }),
    onSuccess: (r) => {
      setTweetText(r.tweet);
      setTweetCopied(false);
    },
    onError: (e) => setFeedback({ kind: 'error', message: describeError(e) }),
  });

  if (isLoading) return <p className={styles.muted}>A carregar…</p>;
  if (error) return <p className={styles.error}>Falha: {describeError(error)}</p>;
  if (!article) return null;

  async function handleCreateStoryFromArticle() {
    if (!article) return;
    try {
      const story = await createStoryMutation.mutateAsync({
        title: article.title,
        description: article.excerpt ?? undefined,
      });
      await addManifestation.mutateAsync({
        storyId: story.id,
        payload: {
          kind: 'wp_post',
          external_id: String(article.id),
          external_url: article.link,
          site: article.site,
          label: 'Artigo principal',
        },
      });
      setFeedback({
        kind: 'success',
        message: `Story criada e ligada: ${story.title}`,
      });
    } catch (e) {
      setFeedback({ kind: 'error', message: describeError(e) });
    }
  }

  function markDirty() {
    setDirty(true);
    setFeedback(null);
  }

  function handlePickMedia(media: MediaItem) {
    setFeaturedMedia(media.id);
    setFeaturedMediaUrl(media.source_url);
    markDirty();
  }

  // Outros sites configurados (exclui o site atual do artigo) — alvos do cross-publish.
  const currentSlug = site;
  const otherSites = (sites ?? []).filter(
    (s) =>
      s.slug !== currentSlug &&
      s.base_url.replace(/^https?:\/\//, '').replace(/\/$/, '') !== article?.site,
  );

  async function copyTweet() {
    const full = article?.link ? `${tweetText}\n\n${article.link}` : tweetText;
    try {
      await navigator.clipboard.writeText(full);
      setTweetCopied(true);
    } catch {
      setFeedback({ kind: 'error', message: 'Não consegui copiar.' });
    }
  }

  async function handleSave(asDraft: boolean) {
    if (!article) return;
    const wasPublished = article.status === 'publish';
    setFeedback({ kind: 'info', message: asDraft ? 'A gravar…' : 'A publicar…' });
    try {
      await updateMutation.mutateAsync({
        id: article.id,
        site,
        payload: {
          title,
          content,
          excerpt,
          status: asDraft ? 'draft' : 'publish',
          featured_media: featuredMedia ?? 0, // 0 = desassocia a capa no WP (F15)
          categories: categoryIds,
          tags: selectedTags.map((t) => t.id),
        },
      });
      setFeedback({
        kind: 'success',
        message: asDraft
          ? 'Rascunho salvo.'
          : wasPublished
            ? 'Artigo republicado.'
            : 'Artigo publicado — agora está no ar.',
      });
      setDirty(false);
    } catch (e) {
      setFeedback({ kind: 'error', message: describeError(e) });
    }
  }

  const handleTagsChange = (ids: number[]) => {
    setSelectedTags((prev) => {
      const known = new Map(prev.map((t) => [t.id, t]));
      (tagSearchResults ?? []).forEach((t) => known.set(t.id, { id: t.id, name: t.name }));
      return ids
        .map((id) => known.get(id) ?? { id, name: `tag #${id}` })
        .filter(Boolean);
    });
    markDirty();
  };

  function insertH3FromPaa(question: string) {
    const block = `<h3>${question}</h3><p>(resposta a desenvolver)</p>`;
    setContent((prev) => (prev ? `${prev}\n${block}` : block));
    markDirty();
    setFeedback({
      kind: 'info',
      message: `H3 adicionado: "${question}"`,
    });
  }

  return (
    <section className={styles.root}>
      <div className={styles.main}>
      <header className={styles.head}>
        {embedded ? (
          <button type="button" className={styles.back} onClick={() => onClose?.()}>
            ✕ Fechar
          </button>
        ) : (
          <Link to="/artigos" className={styles.back}>
            ← Voltar
          </Link>
        )}
        <div className={styles.title}>
          Artigo #{article.id}
          <Badge tone={article.status === 'publish' ? 'ok' : 'warn'} size="sm">
            {article.status}
          </Badge>
        </div>
        <VerNoWordPress
          id={article.id}
          site={site}
          fallbackLink={article.link}
          status={article.status}
        />
      </header>

      <div className={styles.meta}>
        <span>Site: {article.site}</span>
        <span>
          {article.status === 'publish' ? 'Publicado' : 'Rascunho desde'}:{' '}
          {new Date(article.published_at).toLocaleString('pt-BR')}
        </span>
        {article.workflow_id && (
          <span>
            Workflow:{' '}
            <Link to={`/workflows/${article.workflow_id}`} className={styles.link}>
              {article.workflow_id}
            </Link>
          </span>
        )}
        {article.twitter_post_id && (
          <a
            href={`https://x.com/i/status/${article.twitter_post_id}`}
            target="_blank"
            rel="noreferrer"
            className={styles.link}
          >
            Tweet ↗
          </a>
        )}
      </div>

      <div className={styles.field}>
        <label className={styles.label}>Título</label>
        <input
          type="text"
          value={title}
          onChange={(e) => {
            setTitle(e.target.value);
            markDirty();
          }}
          className={styles.input}
        />
      </div>

      <div className={styles.field}>
        <label className={styles.label}>Imagem destacada</label>
        <div className={styles.mediaRow}>
          {featuredMediaUrl ? (
            <img src={featuredMediaUrl} alt="" className={styles.mediaThumb} />
          ) : (
            <div className={styles.mediaPlaceholder}>sem imagem</div>
          )}
          <Button variant="secondary" size="sm" onClick={() => setPickerOpen(true)}>
            Trocar imagem
          </Button>
          {featuredMedia && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setFeaturedMedia(null);
                setFeaturedMediaUrl(null);
                markDirty();
              }}
            >
              Remover
            </Button>
          )}
        </div>
      </div>

      <div className={styles.field}>
        <label className={styles.label}>Excerpt</label>
        <textarea
          value={excerpt}
          onChange={(e) => {
            setExcerpt(e.target.value);
            markDirty();
          }}
          rows={2}
          className={styles.textarea}
        />
      </div>

      <div className={styles.field}>
        <label className={styles.label}>Categorias</label>
        <ChipPicker
          selected={categoryIds}
          options={categoryOptions}
          onChange={(ids) => {
            setCategoryIds(ids);
            markDirty();
          }}
          placeholder="Adicionar categoria…"
          emptyLabel="Nenhuma categoria encontrada"
        />
      </div>

      <div className={styles.field}>
        <label className={styles.label}>Tags</label>
        <ChipPicker
          selected={selectedTags.map((t) => t.id)}
          options={tagOptions}
          onChange={handleTagsChange}
          onSearch={setTagQuery}
          loading={tagSearching}
          placeholder="Pesquisar tags (mín. 2 letras)…"
          emptyLabel={tagQuery.length < 2 ? 'Escreva para procurar' : 'Sem resultados'}
        />
      </div>

      <div className={styles.field}>
        <label className={styles.label}>Story (Axioma I)</label>
        {linkedStories && linkedStories.length > 0 ? (
          <div className={styles.storyLinked}>
            <Badge tone="warn" size="xs">
              story
            </Badge>
            <Link
              to={`/stories/${linkedStories[0].id}`}
              className={styles.storyLinkedTitle}
            >
              {linkedStories[0].title}
            </Link>
            <Badge tone="info" size="xs">
              {linkedStories[0].manifestations_count} manifestações
            </Badge>
            {linkedStories.length > 1 && (
              <span className={styles.muted}>
                (+{linkedStories.length - 1} outra
                {linkedStories.length - 1 === 1 ? '' : 's'})
              </span>
            )}
          </div>
        ) : (
          <div className={styles.storyEmpty}>
            <span className={styles.muted}>
              Este artigo não pertence a nenhuma story.
            </span>
            <Button
              size="sm"
              variant="secondary"
              onClick={handleCreateStoryFromArticle}
              loading={createStoryMutation.isPending || addManifestation.isPending}
            >
              Criar story deste artigo
            </Button>
            <Link to="/stories" className={styles.smallLink}>
              ou ligar a existente →
            </Link>
          </div>
        )}
      </div>

      <div className={styles.field}>
        <label className={styles.label}>Conteúdo</label>
        <RichTextEditor value={content} onChange={(v) => { setContent(v); markDirty(); }} />
      </div>

      {history && history.length > 0 && (
        <details className={styles.history}>
          <summary className={styles.historySummary}>
            Histórico de edições ({history.length})
          </summary>
          <ul className={styles.historyList}>
            {history.map((h) => (
              <li key={h.id} className={styles.historyItem}>
                <span className={styles.historyDate}>
                  {new Date(h.edited_at).toLocaleString('pt-BR')}
                </span>
                <span className={styles.historyFields}>
                  campos: {Object.keys(h.changes).join(', ')}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}

      {/* Twitter — gera o tweet do artigo para revisão (não posta). */}
      <div className={styles.tweetBox}>
        <div className={styles.tweetHead}>
          <span className={styles.tweetTitle}>Tweet do artigo</span>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => genTweet.mutate()}
            loading={genTweet.isPending}
          >
            {tweetText ? 'Gerar outro' : 'Gerar tweet'}
          </Button>
        </div>
        {tweetText && (
          <>
            <textarea
              className={styles.tweetText}
              value={tweetText}
              onChange={(e) => {
                setTweetText(e.target.value);
                setTweetCopied(false);
              }}
              rows={3}
            />
            <div className={styles.tweetFoot}>
              <span
                className={
                  tweetText.length > 280 ? styles.tweetOver : styles.tweetCount
                }
              >
                {tweetText.length}/280
              </span>
              {article?.link && <span className={styles.tweetLink}>+ link: {article.link}</span>}
              <Button size="sm" variant="ghost" onClick={copyTweet}>
                {tweetCopied ? 'Copiado ✓' : 'Copiar tweet + link'}
              </Button>
            </div>
          </>
        )}
      </div>

      <div className={styles.actions}>
        {feedback && (
          <div
            className={`${styles.feedback} ${
              feedback.kind === 'success'
                ? styles.feedbackOk
                : feedback.kind === 'error'
                  ? styles.feedbackError
                  : styles.feedbackInfo
            }`}
          >
            <StatusDot
              state={
                feedback.kind === 'success'
                  ? 'ok'
                  : feedback.kind === 'error'
                    ? 'crit'
                    : 'warn'
              }
            />
            <span>{feedback.message}</span>
          </div>
        )}
        <div className={styles.actionsRight}>
          <Button
            variant="ghost"
            onClick={() => {
              if (!dirty || window.confirm('Descartar alterações não salvas?')) closeOrBack();
            }}
          >
            {embedded ? 'Fechar' : 'Cancelar'}
          </Button>
          <Button
            variant="secondary"
            onClick={() => handleSave(true)}
            disabled={!dirty || updateMutation.isPending}
            loading={updateMutation.isPending && dirty}
          >
            Guardar rascunho
          </Button>
          {article.status === 'publish' ? (
            // já no ar: re-publicar só faz sentido com alterações
            <Button
              onClick={() => handleSave(false)}
              disabled={!dirty || updateMutation.isPending}
              loading={updateMutation.isPending && dirty}
            >
              Re-publicar
            </Button>
          ) : (
            // rascunho: publicar SEMPRE disponível (mesmo sem edições)
            <Button
              onClick={() => handleSave(false)}
              disabled={updateMutation.isPending}
              loading={updateMutation.isPending}
            >
              {updateMutation.isPending ? 'A publicar…' : 'Publicar'}
            </Button>
          )}

          {/* Publicar em OUTRO site configurado. Apagado se não houver outro site. */}
          <div className={styles.crossPublish}>
            <select
              className={styles.crossSelect}
              value={targetSite}
              onChange={(e) => setTargetSite(e.target.value)}
              disabled={otherSites.length === 0 || crossPublish.isPending}
              title={
                otherSites.length === 0
                  ? 'Cadastre outro site em Config → Sites para publicar lá'
                  : 'Escolha o site de destino'
              }
            >
              <option value="">outro site…</option>
              {otherSites.map((s) => (
                <option key={s.slug} value={s.slug}>
                  {s.name}
                </option>
              ))}
            </select>
            <Button
              variant="secondary"
              onClick={() => crossPublish.mutate()}
              disabled={otherSites.length === 0 || !targetSite || crossPublish.isPending}
              loading={crossPublish.isPending}
            >
              Publicar
            </Button>
          </div>
        </div>
      </div>

      <MediaPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onPick={handlePickMedia}
      />
      </div>
      <div className={styles.side}>
        <OrigemPanel id={article.id} site={site} />
        <SeoPanel
          title={title}
          excerpt={excerpt}
          contentHtml={content}
          featuredMediaId={featuredMedia}
          slug={
            typeof article.raw?.slug === 'string'
              ? (article.raw.slug as string)
              : null
          }
          paa={paaReal}
          onInsertH3={insertH3FromPaa}
        />
      </div>
    </section>
  );
}

// Painel de ORIGEM — "Artigo gerado a partir de Quentes · X" + reabre os dados do SERP.
function OrigemPanel({ id, site }: { id: number; site?: string }) {
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ['article', 'origem', id, site ?? ''],
    queryFn: () => articlesApi.origem(id, site),
    staleTime: 60_000,
  });
  if (!data || !data.rotulo) return null; // origem não rastreada (legado/manual) → sem painel
  const query = typeof data.contexto?.query === 'string' ? data.contexto.query : undefined;
  const editoria = typeof data.contexto?.editoria === 'string' ? data.contexto.editoria : undefined;
  return (
    <aside className={styles.origemCard}>
      <span className={styles.origemLabel}>Origem do artigo</span>
      <p className={styles.origemText}>
        Gerado a partir de <strong>{data.rotulo}</strong>
        {data.assunto ? (
          <>
            {' '}· assunto <strong>{data.assunto}</strong>
          </>
        ) : null}
      </p>
      {query ? (
        <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
          Ver dados de origem (SERP)
        </Button>
      ) : (
        <span className={styles.muted}>Sem dados de SERP guardados para esta origem.</span>
      )}
      {open && query && (
        <OrigemModal query={query} editoria={editoria} onClose={() => setOpen(false)} />
      )}
    </aside>
  );
}

// Modal read-only com os dados do SERP que originaram o artigo (mesma fonte do card
// de Inteligência: cobertura por veículo + PAA + palavras-chave). Sem ações de geração
// — o artigo já existe; aqui é só auditoria da origem.
function OrigemModal({
  query,
  editoria,
  onClose,
}: {
  query: string;
  editoria?: string;
  onClose: () => void;
}) {
  const topicQuery = useQuery({
    queryKey: ['intel', 'topic', query],
    queryFn: () => intelligenceService.getTopic({ query, editoria }),
  });
  const detail = topicQuery.data;
  const grouped = useMemo(() => {
    const map = new Map<string, NewsCard[]>();
    for (const n of detail?.news ?? []) {
      const k = n.source ?? 'Outros';
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(n);
    }
    return [...map.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [detail?.news]);

  return (
    <div className={styles.origemBackdrop} onClick={onClose}>
      <aside className={styles.origemModal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.origemModalHead}>
          <h2 className={styles.origemModalTitle}>{query}</h2>
          <button type="button" className={styles.origemModalClose} onClick={onClose}>
            fechar
          </button>
        </header>
        {topicQuery.isLoading && <p className={styles.muted}>A buscar a cobertura do SERP…</p>}
        {topicQuery.isError && (
          <p className={styles.error}>Falha ao buscar os dados de origem.</p>
        )}
        {detail && (
          <div className={styles.origemModalBody}>
            <p className={styles.muted}>
              Dados do SERP que originaram este artigo (somente leitura).
            </p>
            {detail.motivo && (
              <p className={styles.origemVerdict}>
                <strong>{detail.verdict}</strong> — {detail.motivo}
              </p>
            )}

            <div className={styles.origemSection}>
              <span className={styles.origemSectionLabel}>
                Cobertura · {grouped.length} veículos · {detail.news.length} matérias
              </span>
              {grouped.length > 0 ? (
                <div className={styles.origemGroups}>
                  {grouped.map(([source, arts]) => (
                    <div key={source} className={styles.origemGroup}>
                      <div className={styles.origemGroupHead}>
                        {arts[0]?.source_icon && (
                          <img className={styles.origemNewsIcon} src={arts[0].source_icon} alt="" />
                        )}
                        <span className={styles.origemGroupName}>{source}</span>
                        <span className={styles.origemGroupCount}>{arts.length}</span>
                      </div>
                      <ul className={styles.origemGroupList}>
                        {arts.map((n, i) => (
                          <li key={n.link ?? `${n.title}-${i}`}>
                            {n.link ? (
                              <a href={n.link} target="_blank" rel="noopener noreferrer">
                                {n.title}
                              </a>
                            ) : (
                              <span>{n.title}</span>
                            )}
                            {n.date && <span className={styles.origemNewsDate}> · {n.date}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ) : (
                <p className={styles.muted}>Sem cobertura detetada.</p>
              )}
            </div>

            {detail.paa.length > 0 && (
              <div className={styles.origemSection}>
                <span className={styles.origemSectionLabel}>People Also Ask</span>
                <ul className={styles.origemPaa}>
                  {detail.paa.map((q) => (
                    <li key={q}>{q}</li>
                  ))}
                </ul>
              </div>
            )}

            {detail.keywords.length > 0 && (
              <div className={styles.origemSection}>
                <span className={styles.origemSectionLabel}>Palavras-chave (SEO)</span>
                <div className={styles.origemChips}>
                  {detail.keywords.map((k) => (
                    <span key={k} className={styles.origemChip}>
                      {k}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </aside>
    </div>
  );
}

// "Ver no WordPress" — dropdown quando o asset tem +1 publicação (cross-posts).
// Rascunho NÃO mostra link (a URL pública ainda não existe/é 404) — só "não publicado".
function VerNoWordPress({
  id,
  site,
  fallbackLink,
  status,
}: {
  id: number;
  site?: string;
  fallbackLink: string;
  status: string;
}) {
  const isPublished = status === 'publish';
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ['publications', id, site ?? ''],
    queryFn: () => articlesApi.publications(id, site),
    staleTime: 60_000,
    enabled: isPublished, // sem publicação ao vivo → não consulta nem mostra URL
  });

  if (!isPublished) {
    return <span className={styles.draftTag}>Rascunho — ainda não está no ar</span>;
  }

  const pubs = data?.publications ?? [];

  if (pubs.length <= 1) {
    return (
      <a
        href={pubs[0]?.url ?? fallbackLink}
        target="_blank"
        rel="noreferrer"
        className={styles.extLink}
      >
        Ver no WordPress ↗
      </a>
    );
  }
  return (
    <div className={styles.pubWrap}>
      <button
        type="button"
        className={styles.extLink}
        onClick={() => setOpen((o) => !o)}
      >
        Ver no WordPress ({pubs.length}) ▾
      </button>
      {open && (
        <div className={styles.pubMenu}>
          {pubs.map((p) => (
            <a
              key={p.url}
              href={p.url}
              target="_blank"
              rel="noreferrer"
              className={styles.pubItem}
            >
              {p.site}
              {p.current ? ' · atual' : ''} ↗
            </a>
          ))}
        </div>
      )}
    </div>
  );
}
